// Generic, brand-neutral constants shared across the framework's section
// components. Anything brand-specific (colors, copy, images) belongs on the
// BrandDataset (see types.ts) instead — never add a brand's own value here.

// Shared responsive horizontal padding for padded (non-full-bleed) sections.
export const PADX = "px-4 md:px-10 lg:px-[48px] 2xl:px-[80px]";

// Neutral light surface tone (not brand-specific) for text/UI over a dark or
// image-backed scrim.
export const CREAM = "#fffdfa";

/**
 * Runs a horizontal rail through the right page margin below `md`, so the next
 * card is cut off by the VIEWPORT rather than by the page inset.
 *
 * A rail that stops at the text margin reads as a card clipped by mistake; one
 * that runs off the screen edge reads as a rail that continues, which is the
 * whole swipe affordance on touch. The trailing `pr` puts the page's own inset
 * back after the last card, so the end of the scroll lines up with everything
 * above it instead of ending flush against the glass. Cancelled at `md`, where
 * these modules sit inside the margins like everything else.
 *
 * Pairs with `PADX` — change one step and change the other.
 */
export const RAIL_BLEED_RIGHT = "-mr-4 pr-4 md:mr-0 md:pr-0";

/**
 * How much of the next card shows at the right edge of a mobile rail.
 *
 * A full-width card on a phone has no swipe affordance; a sliver of the next
 * one is the whole signal that the row continues. 48px is large enough to
 * read as a card, not a rounding error, and small enough that the card in
 * front still owns the row. Shared so Highly Rated and Behind the Scenes
 * can't pick different peeks and look like two different systems
 * (Ryan, 2026-09-22).
 */
export const RAIL_PEEK = "48px";

/**
 * A section title: Subheading Medium on a phone, Section Heading from `md`.
 *
 * Section Heading (22px) was drawn against English, where these titles are one
 * or two short words — "Best sellers", "Highly rated". Translation is where
 * that breaks: the German and French forms of the same labels run roughly half
 * again as long, and at 22px on a 360px phone they turn a one-line title into
 * two, which pushes every rail below them down and makes the page read as a
 * stack of headings. 18px buys back the width to absorb that growth on one
 * line, and it still clears the 14px body underneath it (Ryan, 2026-09-22).
 *
 * The brand name is deliberately NOT on this step. It stays at Section Heading
 * below `md` so the page has exactly one thing bigger than its section titles,
 * which is the whole point of the header.
 *
 * BOTH STEPS ARE BREAKPOINT-SCOPED — `max-md:` and `md:` — so exactly one is
 * ever live. Each `type-fs-*` is a composite that sets size, weight,
 * line-height and family while declaring only weight `!important`, so a bare
 * token under a `md:` token resolves per property and lands on a hybrid the
 * ramp doesn't contain. Scoping both removes the overlap rather than betting
 * on which declaration wins.
 */
export const SECTION_TITLE_TYPE =
  "max-md:type-fs-subheading-medium md:type-fs-section-heading";

/**
 * The framework's one hover treatment for an image-backed tile: the photo
 * eases up inside its own clipped frame, so the frame's edges and the page's
 * grid never move. Put `TILE_HOVER_GROUP` on the tile's clickable root and
 * `TILE_HOVER_IMAGE` on the `<img>`, inside a wrapper that clips.
 *
 * Keyboard users get the same feedback via `group-focus-visible`, and the
 * motion is dropped for anyone who asked their OS for reduced motion — the
 * scale is decoration, so it is the first thing to go (Ryan, 2026-09-17).
 */
export const TILE_HOVER_GROUP = "group cursor-pointer";

export const TILE_HOVER_IMAGE =
  "transition-transform duration-300 ease-out group-hover:scale-[1.03] group-focus-visible:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100";
